/**
 * The operations-order templates, one per archetype (03 §2), in the terse register of the real
 * thing. `special` is the rule that makes the mission itself, stated plainly: the player must
 * be able to read every condition that can fail the mission before he accepts it (04 §12.3).
 * Pure data and pure functions; briefingText.ts assembles them with the mission parameters.
 */

import type { ArchetypeId, MissionParams, OriginalArchetypeId } from '../../../sim/types';
import { EXPANSION_ORDERS } from './ordersExpansion';

export interface ArchetypeOrder {
  /** What the enemy holds there. */
  situation: string;
  mission: (p: MissionParams) => string;
  execution: string[];
  special: (p: MissionParams) => string[];
  /** What the job cannot be done without, and what to pack beyond standard issue. Printed under EXECUTION. */
  equipment?: string[];
  /** Replaces the standard extraction paragraph where the mission changes how you leave. */
  extraction?: string[];
  /** Added to TIME ON TARGET. */
  time?: string;
}

const fixed = (text: string) => (): string => text;
const rules = (...lines: string[]) => (): string[] => lines;

/** Mission 6's barrier is whatever the terrain makes of "linear water obstacle" (03 §2.1). */
function barrier(p: MissionParams): string {
  if (p.biome === 'desert') {
    return 'THE WADI: QUICKSAND PATCHES SWALLOW A RUNNING MAN. CROSS AT THE ROPE BRIDGE OR PICK YOUR WAY ON FOOT.';
  }
  if (p.biome === 'temperate') {
    return 'THE RIVER: CROSS AT A FORD OR THE BRIDGE. THE DEEP CHANNEL DROWNS A MAN WHO CRAWLS IN IT.';
  }
  return 'THE RIVER: THICK ICE HOLDS. CRACKED ICE DOES NOT - RUN ACROSS IT AND YOU GO THROUGH, AND THAT IS FATAL.'
    + ' THE ENEMY FALLS THROUGH TOO.';
}

/** 03 §2 mission 7: launchers = ceil(TR / 2), at most four. */
export function samLauncherCount(threat: number): number {
  return Math.min(4, Math.max(1, Math.ceil(threat / 2)));
}

const ORIGINAL_ORDERS: Readonly<Record<OriginalArchetypeId, ArchetypeOrder>> = {
  'munitions-depot': {
    situation: 'A WALLED SUPPLY COMPOUND: AMMUNITION SHACK, EXPLOSIVES MAGAZINE, FUEL DUMP. SENTRIES AND'
      + ' MACHINE-GUN NESTS COVER THE GATES.',
    mission: fixed('DESTROY ALL THREE DEPOT BUILDINGS. PARTIAL DESTRUCTION SCORES; THE MISSION IS ALL THREE.'),
    execution: [
      'THE AMMUNITION SHACK CAN BE LOOTED: SHOOT THE DOOR, TAKE WHAT YOU NEED, THEN BLOW IT.',
      'SECONDARY EXPLOSIONS CHAIN. BE MORE THAN 12 M AWAY WHEN IT GOES UP.',
    ],
    special: rules(
      'THE MAGAZINE IS BUNKER-CLASS: ONLY A LAW OR A TIME BOMB WILL DESTROY IT. THE CARBINE AND GRENADES WILL NOT.',
      'NO NOISE RESTRICTION ON THIS ONE.',
    ),
  },
  'code-book': {
    situation: 'A RUINED, WALLED VILLAGE WITH A COMMUNICATIONS POST UNDER THE TALL AERIAL. ELITE GARRISON,'
      + ' EXPECTING TROUBLE: THEY START ON ALERT.',
    mission: fixed('REACH THE RADIO INSIDE THE COMMUNICATIONS POST, TAKE THE CODE BOOK, AND GET OUT WITH IT.'),
    execution: [
      'FIND THE AERIAL ON THE PASS OVER THE TARGET.',
      'SHORT SIGHTLINES: WORK THE DOORWAYS AND THE RUBBLE.',
    ],
    special: rules(
      'DO NOT DESTROY THE COMMUNICATIONS POST BEFORE YOU HAVE THE BOOK. THE BOOK BURNS WITH IT AND THE MISSION'
        + ' HAS FAILED.',
      'A FULL ALARM PUTS TWO ELITES INSIDE THE POST.',
    ),
  },
  'enemy-aircraft': {
    situation: 'AN AIRSTRIP RINGED BY UNBROKEN WIRE. JETS PARKED ON THE APRON.',
    mission: fixed('DAMAGE OR DESTROY EVERY PARKED AIRCRAFT.'),
    execution: [
      'BREACH THE WIRE: THERE IS NO GAP.',
      'ONE GRENADE, LAW OR BOMB PER AIRCRAFT. A BOMB BETWEEN TWO WINGTIPS TAKES BOTH.',
    ],
    special: rules(
      'KNIFE ONLY UNTIL YOU ARE INSIDE THE WIRE. IF THE GARRISON IS ALERTED BEFORE THAT, THE JETS SCRAMBLE:'
        + ' 30 SECONDS OF ENGINE START, THEN AN EMPTY RUNWAY AND A FAILED MISSION.',
      'REACH THE WIRE WITHOUT CONTACT FOR THE NO-CONTACT BONUS.',
    ),
  },
  'capture-officer': {
    situation: 'A HEADQUARTERS CAMP OF TENTS. THE OFFICER IS IN ONE OF THEM.',
    mission: fixed('TAKE THE ENEMY OFFICER ALIVE AND BRING HIM OUT.'),
    execution: [
      'LOOK INTO A TENT FROM THE FLAP BEFORE YOU COMMIT.',
      'CLOSE ON HIM WITH A WEAPON UP AND HE SURRENDERS. HE WALKS WITH YOU.',
    ],
    special: rules(
      'HE MUST LIVE. WOUNDED, HE IS WORTH HALF. DEAD, THE MISSION HAS FAILED. NO EXPLOSIVES NEAR THE TENTS.',
      'AT FULL ALARM HE RUNS NORTH FOR A VEHICLE. YOU HAVE 60 SECONDS.',
    ),
    extraction: [
      'ON CAPTURE THE PICKUP MOVES TO WHERE YOU TOOK HIM. THE ORIGINAL X STAYS OPEN AS THE ALTERNATE.',
      'RECALL AS NORMAL: 45 SECONDS OUT, 20 SECONDS ON THE LADDER. THE OFFICER BOARDS BEFORE YOU.',
    ],
  },
  pipeline: {
    situation: 'AN ARMOURED PUMPING STATION ON A PIPELINE THAT CROSSES THE AREA EAST TO WEST. MINEFIELDS RING IT.',
    mission: fixed('DESTROY THE PUMPING STATION.'),
    execution: [
      'FIND OR MAKE A LANE THROUGH THE MINES. A GRENADE CLEARS ONE.',
      'THE PIPE IS WAIST HIGH: CRAWL UNDER IT AT THE MARKED SUPPORTS.',
      'AUTOMATED MINITANKS WAKE WHEN YOU COME WITHIN 40 M OF THE STATION. THEY DO NOT GIVE UP AND A TRENCH DOES'
        + ' NOT HIDE YOU. A LAW OR A BOMB STOPS ONE.',
    ],
    special: rules(
      'ONLY A TIME BOMB WILL DESTROY THE STATION. NO BOMB, NO MISSION: CARRY ONE AND PACK A SPARE IN A POD.',
    ),
  },
  'radar-array': {
    situation: 'A RADAR ARRAY BEYOND A WATER OBSTACLE THAT SPANS THE WHOLE AREA. ELITE GARRISON.',
    mission: fixed('KNOCK OUT THE RADAR ANTENNAS. HALF OF THEM IS SUCCESS; ALL OF THEM IS THE BONUS.'),
    execution: [
      'A GRENADE IS ENOUGH FOR AN ANTENNA.',
      'DROP YOUR PODS WELL NORTH AND RUN LIGHT.',
    ],
    special: (p) => [barrier(p), 'AT HIGHER THREAT THE BRIDGE IS GUARDED.'],
    time: 'THE CLOCK IS TIGHT ON THIS ONE. DO NOT LOITER SOUTH OF THE OBSTACLE.',
  },
  'sam-site': {
    situation: 'A SURFACE-TO-AIR MISSILE SITE GUARDED BY AUTOMATED MINI-BUNKERS.',
    mission: (p) => {
      const n = samLauncherCount(p.threat);
      return n === 1 ? 'DESTROY THE SAM LAUNCHER.' : `DESTROY ALL ${n} SAM LAUNCHERS.`;
    },
    execution: [
      'THE MINI-BUNKERS CYCLE LIVE AND DORMANT, AND FIRE ON ANYTHING MOVING FASTER THAN A CRAWL - THEIR OWN MEN'
        + ' INCLUDED. TIME THE CYCLE.',
    ],
    special: rules(
      'KNIFE ONLY UNTIL YOU ARE INSIDE THE SITE. ONE SHOT OR ONE EXPLOSION OUTSIDE IT COSTS 1,000 POINTS AND'
        + ' EVERY KILL POINT YOU HAVE. BEING SEEN IS NOT CONTACT. SHOOTING IS.',
      'REACH THE SITE UNHEARD: +750.',
    ),
  },
  'pow-camp': {
    situation: 'A PRISON CAMP. OUR MEN ARE HELD IN TIGER PITS, OPENED BY A LEVER SEALED UNDER A CONCRETE'
      + ' CONTROLS PYRAMID.',
    mission: fixed('FREE THE PRISONERS AND BRING EVERY ONE OF THEM OUT.'),
    execution: [
      'BLOW THE CONTROLS PYRAMID: GRENADE, LAW OR BOMB.',
      'KICK THE LEVER TO OPEN THE PITS.',
      'RECALL, THEN HOLD THE LADDER.',
    ],
    special: rules(
      'NOT ONE SHOT AND NOT ONE EXPLOSION BEFORE YOU ARE INSIDE THE CAMP. IF THEY HEAR YOU COMING THE PRISONERS'
        + ' ARE MOVED, THE MISSION HAS FAILED, AND THERE IS NO SECOND ATTEMPT.',
    ),
    extraction: [
      'RECALL AS NORMAL: 45 SECONDS OUT, 20 SECONDS ON THE LADDER.',
      'THE PRISONERS BOARD BEFORE YOU, ONE SECOND EACH. DEFEND THE LADDER UNTIL THE LAST MAN IS UP.',
    ],
  },
  'photograph-aircraft': {
    situation: 'A HANGAR HOLDING AN EXPERIMENTAL AIRCRAFT. A GUARDHOUSE STANDS NEAR IT.',
    mission: fixed('GET INSIDE THE HANGAR AND STAY THERE 20 SECONDS, UNINTERRUPTED, WHILE THE CAMERA WORKS.'
      + ' THEN GET OUT.'),
    execution: [
      'AN ENEMY UNIFORM FROM THE GUARDHOUSE IS THE INTENDED WAY IN. THE OTHER IS PERFECT TIMING BETWEEN SENTRY'
        + ' SWEEPS.',
    ],
    special: rules(
      'ANYONE WHO SEES YOU GO IN FOLLOWS YOU IN, AND THE 20 SECONDS START AGAIN.',
      'ONLY THE LAST 25 M TO THE HANGAR MATTERS: DO NOT BE SEEN THERE. A UNIFORM LASTS UNTIL YOU FIRE.',
    ),
    time: 'ALLOW FOR 20 SECONDS INSIDE THE HANGAR.',
  },
  hostages: {
    situation: 'A CONCRETE PRISON WITH A GUARD HOUSE BESIDE IT. THE PRISON IS WIRED FOR DEMOLITION.',
    mission: fixed('OPEN THE PRISON AND BRING THE HOSTAGES OUT ALIVE.'),
    execution: [
      'THE DOOR IS REINFORCED: GRENADE, LAW OR BOMB. THE CARBINE WILL NOT OPEN IT.',
    ],
    special: rules(
      'A FULL ALARM BEFORE THE DOOR IS OPEN STARTS A 40-SECOND DEMOLITION COUNT. KILL THE OFFICER HOLDING THE'
        + ' DETONATOR IN THE GUARD HOUSE TO STOP IT.',
      'THE MOMENT THE DOOR OPENS, ROCKET TROOPERS COME IN FROM THE PERIMETER FOR THE HOSTAGES.',
    ),
    extraction: [
      'RECALL FIRST, THEN BLOW THE DOOR. THE OSPREY NEEDS 45 SECONDS AND THE HOSTAGES DO NOT HAVE LONGER.',
      'THE HOSTAGES BOARD BEFORE YOU. DEFEND THE LADDER.',
    ],
  },
  diversion: {
    situation: 'THE BORDER BELT: TURRET BUNKERS, WIRE, MINES AND PROXIMITY BOMBS. THE DENSEST DEFENCES YOU'
      + ' WILL SEE.',
    mission: fixed('DO AS MUCH DAMAGE AS YOU CAN AND BE AT THE NORTHERN BORDER, ALIVE, WHEN THE CLOCK RUNS OUT.'),
    execution: [
      'LIE LOW UNTIL THE SIGNAL: A TONE SHORTLY AFTER TOUCHDOWN. FIRE BEFORE IT AND YOU LOSE 1,000 POINTS.',
      'AFTER THE SIGNAL, MAKE NOISE. SIXTY QUIET SECONDS BRINGS A WARNING TONE; THIRTY MORE AND THE MISSION'
        + ' HAS FAILED.',
      'EMPLACEMENTS SCORE HALF AGAIN.',
    ],
    special: rules('NO EARLY RECALL. THE OSPREY COMES AT CLOCK ZERO AND NOT A SECOND BEFORE.'),
    extraction: [
      'RECALL IS DISABLED. THE OSPREY ARRIVES AT THE BORDER WHEN THE CLOCK REACHES ZERO AND HOLDS 20 SECONDS.',
      'BE THERE. THE SOUTH EDGE IS A LONG WALK.',
    ],
    time: 'YOU ARE ON THE GROUND FOR THE WHOLE CLOCK. PACE YOUR AMMUNITION.',
  },
  'delayed-sabotage': {
    situation: 'AN AVIATION FUEL DUMP. GUARDHOUSES ON THE APPROACHES.',
    mission: fixed('PLANT A TIME BOMB AT THE FUEL DUMP AND LEAVE WITHOUT ANYONE KNOWING YOU CAME.'),
    execution: [
      'EVERY BOMB IS PRE-SET TO MIDNIGHT. IT GOES OFF AFTER YOU ARE GONE, WHATEVER FUSE YOU CHOOSE.',
      'A GUARD CAUGHT INSIDE A GUARDHOUSE GIVES YOU A UNIFORM. THAT IS THE INTENDED WAY IN.',
    ],
    special: rules(
      'IF YOU ARE SEEN WITHIN 25 M OF THE DUMP - BEFORE OR AFTER PLANTING - THE BOMB IS FOUND AND THE MISSION'
        + ' HAS FAILED.',
      'A FULL ALARM ANYWHERE DOUBLES THE SENTRIES ON THE DUMP.',
      'NO BOMB, NO MISSION: CARRY A TIME BOMB.',
    ),
  },
};

/** All thirty-six: the 1987 twelve above, and the expansion's orders, by category, from ordersExpansion.ts. */
export const ORDERS: Readonly<Record<ArchetypeId, ArchetypeOrder>> = { ...ORIGINAL_ORDERS, ...EXPANSION_ORDERS };
